<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted, ref } from 'vue'
import LocalUnlockScreen from './components/account/LocalUnlockScreen.vue'
import LoginScreen from './components/account/LoginScreen.vue'
import BeamLoader from './components/app/BeamLoader.vue'
import UpdateNotice from './components/app/UpdateNotice.vue'
import { useAuthStore } from './stores/auth'
import { secureStorage } from './services/crypto/secureStorage'
import { IS_DEMO } from './demo/demoMode'

const props = defineProps<{
  mobile: boolean
}>()

const auth = useAuthStore()
const restoringSession = ref(true)
const showSplashLoader = ref(false)

// A live network session is the normal path. A locally unlocked key session is
// also sufficient because restoreKeySession only unlocks it after validating
// the signed, device-bound offline entitlement certificate.
// The demo enters once its session is fully set up, not when its key alone
// unlocks, or the editor would open before the demo corpus exists.
const demoReady = ref(false)
const mayEnterWritingApp = computed(() => (IS_DEMO ? demoReady.value : !auth.locallyLocked && (auth.isAuthenticated || auth.isKeySessionUnlocked)))

const DesktopApp = defineAsyncComponent(() => import('./App.vue'))
const MobileApp = defineAsyncComponent(() => import('./AppMobile.vue'))
const WritingApp = computed(() => (props.mobile ? MobileApp : DesktopApp))

// Preload the appropriate app component chunk while auth is bootstrapping
if (props.mobile) {
  void import('./AppMobile.vue')
} else {
  void import('./App.vue')
}

onMounted(async () => {
  const loaderTimer = setTimeout(() => {
    if (restoringSession.value && !mayEnterWritingApp.value) {
      showSplashLoader.value = true
    }
  }, 250)

  if (IS_DEMO) {
    const { startDemoSession } = await import('./demo/demoSession')
    await startDemoSession()
    demoReady.value = true
    clearTimeout(loaderTimer)
    restoringSession.value = false
    return
  }

  // Local restoration completes independently of cloud requests. If no
  // local session is available, show login after a short bounded splash while
  // the cloud attempt continues. Development sign-in waits for that attempt.
  let splashTimer: ReturnType<typeof setTimeout> | undefined
  const startup = auth.bootstrapSession().catch((error) => {
    console.warn('[AppGate] Session bootstrap failed:', error)
  })
  void startup.then(signInDevAccount).catch((error) => {
    console.warn('[AppGate] Development sign-in failed:', error)
  })
  try {
    await Promise.race([
      startup,
      new Promise((resolve) => { splashTimer = setTimeout(resolve, 2500) }),
    ])
  } finally {
    clearTimeout(loaderTimer)
    clearTimeout(splashTimer)
    restoringSession.value = false
  }
})

// Development-only: signs in to the throwaway account named in .env.claude.local
// (VITE_DEV_USERNAME / VITE_DEV_PASSWORD) so the app can be exercised without
// the login screen. Only `npm run dev:claude` (--mode claude, port 5199) loads
// that file, so other dev servers show the normal login. An explicit sign-out
// on that origin is respected. import.meta.env.DEV is false in every
// `vite build`, so this branch, and the credentials with it, is removed from
// shipped apps.
async function signInDevAccount() {
  if (!import.meta.env.DEV || import.meta.env.MODE !== 'claude') return
  if (mayEnterWritingApp.value || auth.locallyLocked) return
  if (secureStorage.getCloudState() === 'signed_out') return
  const username = import.meta.env.VITE_DEV_USERNAME
  const password = import.meta.env.VITE_DEV_PASSWORD
  if (!username || !password) return
  console.warn(`[AppGate] Development sign-in as "${username}" from .env.claude.local.`)
  await auth.signIn(username, password).catch((error) => {
    console.warn('[AppGate] Development sign-in failed:', error)
  })
}
</script>

<template>
  <component :is="WritingApp" v-if="mayEnterWritingApp" />
  <LocalUnlockScreen v-else-if="!restoringSession && auth.locallyLocked" />
  <LoginScreen v-else-if="!restoringSession" />
  <div v-else class="app-gate-splash" aria-hidden="true">
    <BeamLoader v-if="showSplashLoader" class="app-gate-loader" :size="112" />
  </div>
  <UpdateNotice />
</template>

<style scoped>
.app-gate-splash {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--loading-surface, var(--bg-color, var(--surface-bg, #123f3a)));
  z-index: 99999;
}

/* Before any theme is applied: the logo's own colors, a coral beam on the deep ground. */
.app-gate-loader {
  --beam-trace: #ff6b45;
  --beam-head: #ffd9c9;
  --beam-ghost: #f0e3c4;
}
</style>
